import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Loader2, X } from 'lucide-react';
import { FACES, faceKey, perpendicular } from './faces';
import { VIEWPORT_SELECTOR } from './MeshViewer';
import { rotateJob, type Job3D, type OrientFace } from './threeDApi';
import { useLanguage } from '../../contexts/LanguageContext';

// TURNING THE MESH BY HAND, by naming faces rather than by pressing quarter turns.
//
// The other door into the question the model answers next door. A call reading photographs is
// how it gets answered when nobody is looking at the part; this is for the times somebody IS —
// the leg is plainly lying on its side, and saying so is two sentences, not a minute of
// inference and a paragraph about why.
//
// SAID IN THE SAME TERMS THE MODEL ANSWERS IN, which is what this dialog is for. It used to be
// six buttons: ±90° about X, Y and Z. Every one of them asks somebody to plan a rotation in
// their head — to work out which axis takes the face they are looking at to the place they want
// it — and to press until the picture agrees. What a person actually knows is "the side it calls
// the top is really the back", and that is a sentence, not an axis. So the sentence is what gets
// typed: two of them, and the server derives the one turn that makes them both true (see
// mesh_orient.rotation_for, which is the very function the model's answer goes through).
//
// TWO SENTENCES AND NOT SIX, which is the part worth defending. Six independent face names admit
// answers that are not rotations at all — front and back on the same side, or a left-handed set —
// and there would be no way to reject one. Two faces at right angles, carried onto two sides at
// right angles, are exactly one of the 24 axis-aligned orientations, every time, and the other
// four faces fall out of them. Hence the second row: it is not extra work, it is what makes the
// first row answerable.
//
// AND THE SECOND ROW ONLY OFFERS WHAT STILL DETERMINES A TURN. Once the first row names a face,
// that face and the one opposite it are struck off the second — two faces of ONE axis describe
// no rotation — and the same on the other side of the arrow, because the top and the bottom of a
// product are not two different sides for one face to become. Refusing after the fact is the
// server's job; not offering it is this one's.
//
// RIGHT ANGLES ONLY comes free here, where the old buttons had to be told: a face name IS an
// axis and a sign, so nothing off the axes is sayable. That matters because the six words on a
// part's box are assigned by axis and sign — see meshAnnotations.FACES — and an orientation at
// 43° to them makes every one a lie about the face it names.
//
// Escape closes THIS and not the mesh behind it, which needs the capture phase: MeshModal
// listens on the document too and would otherwise take the whole viewer away when somebody
// meant to dismiss a small dialog. First listener down the tree wins.

// What a second row may still say, given what the first one said — see faces.perpendicular,
// which is the rule and the reason for it.
const stillOpen = perpendicular;

// One sentence: the face as the file has it now, and the side of the product it really is.
type Pair = { from: OrientFace | ''; to: OrientFace | '' };

const complete = (p: Pair) => !!p.from && !!p.to;

function FaceSelect({ value, options, disabled, label, onChange }: {
  value: OrientFace | '';
  options: OrientFace[];
  disabled: boolean;
  label: string;
  onChange: (face: OrientFace | '') => void;
}) {
  const { t } = useLanguage();
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => onChange(e.target.value as OrientFace | '')}
      className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 disabled:opacity-50"
    >
      <option value="">{t('notice.3d.turnPick')}</option>
      {options.map((face) => (
        <option key={face} value={face}>{t(faceKey(face))}</option>
      ))}
    </select>
  );
}

export function MeshRotateModal({ jobId, onChanged, onClose }: {
  jobId: number;
  // The run as it stands after the turn — the same call every other edit here makes. It is
  // what re-addresses the mesh in the viewer, so it is also what makes the part swing.
  onChanged: (job: Job3D) => void;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const [first, setFirst] = useState<Pair>({ from: '', to: '' });
  const [second, setSecond] = useState<Pair>({ from: '', to: '' });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // CLOSING ON A CLICK OUTSIDE, BY LISTENING RATHER THAN BY CATCHING. The usual backdrop —
  // a full-screen div with onClose on it — is the thing that made the mesh untouchable while
  // this was open: every press landed on the catcher instead of on the canvas, and naming the
  // faces of a part you cannot turn round is naming faces you cannot see. So the overlay lets
  // the pointer through (pointer-events-none) and the dismissal is a document listener that
  // asks WHERE the click was.
  //
  // TWO PLACES THAT DO NOT CLOSE IT: this panel, and the viewer's own white panel — which is
  // marked for the purpose, see MeshViewer. Orbiting the part is the reason this dialog is
  // worth leaving open, and a drag that begins on the mesh must not dismiss the thing it is
  // being dragged for.
  //
  // AND THE CLICK THAT CLOSES IT IS SWALLOWED, in the capture phase, before React's own
  // listeners at the root see it. Anywhere else on this screen is the mesh modal's backdrop
  // or its controls: unswallowed, one click away from the dialog would also close the viewer
  // behind it, or press whatever button it happened to land on. Dismissing is the whole of
  // what that click does — the same bargain Escape makes one line up.
  //
  // JUDGED ON WHERE THE PRESS BEGAN as well as on where it ended. An orbit is a drag, and a
  // drag across a part fills the viewport and runs off it — the click that follows is
  // reported against whatever the two ends have in common, which for a wide sweep is a
  // container well outside the mesh. Closing on that would mean the dialog shuts whenever
  // somebody turns the part far enough to see the face they were about to name.
  useEffect(() => {
    let began: Element | null = null;
    const spared = (el: Element | null) =>
      !!el && (!!panel.current?.contains(el) || !!el.closest?.(VIEWPORT_SELECTOR));

    const onDown = (e: PointerEvent) => { began = e.target as Element | null; };
    const onClick = (e: MouseEvent) => {
      // Read once and forgotten, so a click with no press behind it — Enter on a focused
      // button — is judged on itself rather than on where the last drag started.
      const from = began;
      began = null;
      if (spared(from) || spared(e.target as Element | null)) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [onClose]);

  // CHANGING THE FIRST ROW UNSAYS A SECOND ROW IT NO LONGER ALLOWS. The half of the second row
  // that is still open is kept and the half that is not is cleared — silently, because the
  // alternative is a dialog holding a pair of faces it would refuse to send, and the only way
  // out of that is to notice the dropdown you did not touch.
  const sayFirst = (key: keyof Pair, face: OrientFace | '') => {
    const next = { ...first, [key]: face };
    setFirst(next);
    setSecond((s) => ({
      from: stillOpen(next.from).includes(s.from as OrientFace) ? s.from : '',
      to: stillOpen(next.to).includes(s.to as OrientFace) ? s.to : '',
    }));
    setProblem('');
  };

  const ready = complete(first) && complete(second);

  // ONE CALL, NOT A SITTING OF THEM. Both sentences go up together because either alone is not
  // a rotation — which is also why there is a button here where the quarter turns had none.
  const turn = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setProblem('');
    try {
      onChanged(await rotateJob(jobId, [
        { from: first.from as OrientFace, to: first.to as OrientFace },
        { from: second.from as OrientFace, to: second.to as OrientFace },
      ]));
      // WIPED, because the mesh has moved and the sentence has not. "The top is really the
      // back" was true of the file as it was a moment ago; left in the boxes it now reads as a
      // claim about the file as it is, which is the one thing it is certainly not.
      setFirst({ from: '', to: '' });
      setSecond({ from: '', to: '' });
    } catch {
      setProblem(t('notice.3d.turnFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    // Not the shared overlayCls, which pads left of the desktop sidebar so a dialog centres
    // in the app's content area. This one opens INSIDE the mesh modal, which centres in the
    // window — the same offset twice would sit it off to the right of what it belongs to.
    //
    // AND THE BACKDROP IS CLEAR, which is the one thing this dialog needs that the others do
    // not: what it changes is the picture behind it. A scrim over the mesh would dim the only
    // thing worth looking at while naming its faces.
    //
    // LOW ON THE SCREEN, not in the middle of it. Every other dialog here is the thing you
    // are looking at; this one is a set of handles on the thing BEHIND it, and centred it
    // would cover the part whose sides are the only reason to open it.
    //
    // AND IT DOES NOT TAKE THE POINTER. The layer is there to place the panel and nothing
    // else — everything it covers stays clickable and draggable, which for the mesh above is
    // the point. What dismissing looks like is the listener above, not this.
    <div className="pointer-events-none fixed inset-0 z-50 flex items-end justify-center p-4 pb-24">
      <div
        ref={panel}
        role="dialog"
        // NOT aria-modal: it does not make the rest of the screen inert and it is not
        // supposed to. Saying otherwise tells a screen reader the mesh behind is unreachable
        // at the moment somebody is being asked to look at it.
        className="pointer-events-auto relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-2xl backdrop-blur"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('notice.3d.turnClose')}
          className="absolute right-2 top-2 rounded p-1 text-slate-400 transition-colors hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="mb-1 text-center text-sm font-semibold text-slate-900">
          {t('notice.3d.turnTitle')}
        </h2>
        <p className="mb-3 text-center text-[11px] leading-snug text-slate-500">
          {t('notice.3d.turnHint')}
        </p>

        <div className="flex flex-col gap-2">
          {/* THE FACE, AN ARROW, AND WHAT IT REALLY IS. The arrow is the sentence's verb: two
              dropdowns side by side would read as a pair of unrelated choices, and which of
              them is the file's name and which is the product's is the whole of the meaning. */}
          <div className="flex items-center gap-2">
            <FaceSelect
              value={first.from}
              options={FACES}
              disabled={busy}
              label={t('notice.3d.turnFace')}
              onChange={(f) => sayFirst('from', f)}
            />
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <FaceSelect
              value={first.to}
              options={FACES}
              disabled={busy}
              label={t('notice.3d.turnBecomes')}
              onChange={(f) => sayFirst('to', f)}
            />
          </div>

          {/* THE SECOND ROW IS VISIBLE AND DEAD until the first one is a sentence. Visible,
              because it is not optional and a row that appeared on completion would read as a
              consequence of getting the first one right rather than as half of the answer;
              dead, because until the first row is said there is nothing to strike off it. */}
          <div className={`flex items-center gap-2 ${complete(first) ? '' : 'opacity-50'}`}>
            <FaceSelect
              value={second.from}
              options={stillOpen(first.from)}
              disabled={busy || !complete(first)}
              label={t('notice.3d.turnFace')}
              onChange={(f) => { setSecond((s) => ({ ...s, from: f })); setProblem(''); }}
            />
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <FaceSelect
              value={second.to}
              options={stillOpen(first.to)}
              disabled={busy || !complete(first)}
              label={t('notice.3d.turnBecomes')}
              onChange={(f) => { setSecond((s) => ({ ...s, to: f })); setProblem(''); }}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => void turn()}
          disabled={!ready || busy}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-2 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-blue-500 hover:text-blue-600 disabled:opacity-50 disabled:hover:border-slate-300 disabled:hover:text-slate-600"
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {t('notice.3d.turnApply')}
        </button>

        {problem && (
          <p className="mt-3 text-center text-[11px] text-rose-600">
            {problem}
          </p>
        )}
      </div>
    </div>
  );
}
